import { useState } from 'react';
import { useCreateReceipt, useUpdateReceipt, useDeleteReceipt, useUploadReceiptFile } from '../../hooks/useExpenses';
import { getReceiptDownloadUrl } from '../../services/api/expenses';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import ReceiptUpload from './ReceiptUpload';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { parseDecimalInput } from '../../utils/parseDecimalInput';
import type { ExpenseReceipt } from '../../services/api/expenses';
import { getExchangeRates, convertToCZK, COMMON_CURRENCIES } from '../../services/exchangeRate';
import { DateField } from '../ui/DateField';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

interface ReceiptListProps {
  sheetId: number;
  receipts: ExpenseReceipt[];
  editable: boolean;
}

export default function ReceiptList({ sheetId, receipts, editable }: ReceiptListProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReceipt, setNewReceipt] = useState({
    description: '',
    receiptDate: '',
    amount: '',
    currency: 'CZK',
    originalAmount: '',
    exchangeRate: '',
  });
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [editingReceiptId, setEditingReceiptId] = useState<number | null>(null);
  const [editReceipt, setEditReceipt] = useState({ description: '', receiptDate: '', amount: '' });
  const [error, setError] = useState<string | null>(null);
  const [loadingExchangeRate, setLoadingExchangeRate] = useState(false);

  const createMutation = useCreateReceipt();
  const updateMutation = useUpdateReceipt();
  const deleteMutation = useDeleteReceipt();
  const uploadMutation = useUploadReceiptFile();

  const handleRecalculateCurrency = async () => {
    if (!newReceipt.receiptDate || !newReceipt.originalAmount || !newReceipt.currency) {
      setError('Please enter date, amount, and select currency first');
      return;
    }

    setLoadingExchangeRate(true);
    setError(null);

    try {
      const rates = await getExchangeRates(newReceipt.receiptDate);
      const rate = rates.get(newReceipt.currency);

      if (!rate) {
        throw new Error(`Exchange rate not found for ${newReceipt.currency}`);
      }

      const originalAmt = parseDecimalInput(newReceipt.originalAmount) ?? 0;
      const czkAmount = convertToCZK(originalAmt, newReceipt.currency, rate);

      setNewReceipt((prev) => ({
        ...prev,
        exchangeRate: rate.toFixed(4),
        amount: czkAmount.toFixed(2),
      }));
    } catch (err) {
      setError((err as Error).message || 'Failed to fetch exchange rate');
    } finally {
      setLoadingExchangeRate(false);
    }
  };

  const submitReceipt = async () => {
    setError(null);

    try {
      await createMutation.mutateAsync({
        sheetId,
        input: {
          description: newReceipt.description || undefined,
          receiptDate: newReceipt.receiptDate || undefined,
          amount: newReceipt.amount ? (parseDecimalInput(newReceipt.amount) ?? undefined) : undefined,
        },
      });
      setNewReceipt({
        description: '',
        receiptDate: '',
        amount: '',
        currency: 'CZK',
        originalAmount: '',
        exchangeRate: '',
      });
      setShowAddForm(false);
    } catch (err) {
      setError((err as Error).message || 'Failed to add receipt');
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReceipt.amount) return;
    await submitReceipt();
  };

  const handleDelete = async () => {
    if (confirmDeleteId === null) return;
    setError(null);
    try {
      await deleteMutation.mutateAsync({ sheetId, receiptId: confirmDeleteId });
      setConfirmDeleteId(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to delete receipt');
      setConfirmDeleteId(null);
    }
  };

  const handleUpload = async (receiptId: number, file: File) => {
    setError(null);
    try {
      await uploadMutation.mutateAsync({ sheetId, receiptId, file });
    } catch (err) {
      setError((err as Error).message || 'Failed to upload receipt file');
    }
  };

  const startEdit = (receipt: ExpenseReceipt) => {
    setEditingReceiptId(receipt.id);
    setEditReceipt({
      description: receipt.description || '',
      receiptDate: receipt.receiptDate ? receipt.receiptDate.slice(0, 10) : '',
      amount: receipt.amount !== null ? String(receipt.amount) : '',
    });
    setError(null);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingReceiptId === null) return;
    setError(null);
    try {
      await updateMutation.mutateAsync({
        sheetId,
        receiptId: editingReceiptId,
        input: {
          description: editReceipt.description || undefined,
          receiptDate: editReceipt.receiptDate || undefined,
          amount: editReceipt.amount ? (parseDecimalInput(editReceipt.amount) ?? undefined) : undefined,
        },
      });
      setEditingReceiptId(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to update receipt');
    }
  };

  const handleDownload = async (receiptId: number) => {
    const url = await getReceiptDownloadUrl(sheetId, receiptId);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Receipts ({receipts.length})
        </h3>
        {editable && !showAddForm && (
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            Add Receipt
          </Button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && editable && (
        <form onSubmit={handleAdd} className="p-4 border-b border-gray-200 bg-gray-50 space-y-3">
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input
                type="text"
                value={newReceipt.description}
                onChange={(e) => setNewReceipt((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="What was purchased"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <DateField
                value={newReceipt.receiptDate}
                onChange={(nextDate) => setNewReceipt((prev) => ({ ...prev, receiptDate: nextDate }))}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
              <select
                value={newReceipt.currency}
                onChange={(e) => setNewReceipt((prev) => ({ ...prev, currency: e.target.value, exchangeRate: '', amount: '' }))}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              >
                {COMMON_CURRENCIES.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code} - {curr.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Amount Field */}
          {newReceipt.currency === 'CZK' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount in CZK</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newReceipt.amount}
                onChange={(e) => setNewReceipt((prev) => ({ ...prev, amount: e.target.value, originalAmount: e.target.value }))}
                placeholder="0.00"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
          ) : (
            /* Currency Conversion Section */
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Amount in {newReceipt.currency}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newReceipt.originalAmount}
                    onChange={(e) => setNewReceipt((prev) => ({ ...prev, originalAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleRecalculateCurrency}
                  isLoading={loadingExchangeRate}
                  disabled={!newReceipt.receiptDate || !newReceipt.originalAmount}
                >
                  Recalculate to CZK
                </Button>
              </div>

              {/* Show conversion details */}
              {newReceipt.exchangeRate && newReceipt.amount && (
                <div className="text-sm text-gray-700 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Exchange rate:</span>
                    <span className="font-medium">1 {newReceipt.currency} = {newReceipt.exchangeRate} CZK</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold text-blue-700">
                    <span>Amount in CZK:</span>
                    <span>
                      {newReceipt.originalAmount} {newReceipt.currency} × {newReceipt.exchangeRate} = {formatNumber(parseFloat(newReceipt.amount), { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)} Kč
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              type="button"
              isLoading={createMutation.isPending}
              onClick={submitReceipt}
              disabled={!newReceipt.amount}
            >
              Add
            </Button>
            <Button size="sm" variant="secondary" type="button" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Receipt List */}
      {receipts.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>No receipts yet. {editable ? 'Add one to get started.' : ''}</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {receipts.map((receipt) => (
            <div key={receipt.id} className="p-4">
              {editingReceiptId === receipt.id ? (
                <form onSubmit={handleUpdate} className="space-y-3">
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={editReceipt.description}
                        onChange={(e) => setEditReceipt((prev) => ({ ...prev, description: e.target.value }))}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                      <DateField
                        value={editReceipt.receiptDate}
                        onChange={(nextDate) => setEditReceipt((prev) => ({ ...prev, receiptDate: nextDate }))}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Amount (CZK)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editReceipt.amount}
                        onChange={(e) => setEditReceipt((prev) => ({ ...prev, amount: e.target.value }))}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" type="submit" isLoading={updateMutation.isPending}>
                      Save
                    </Button>
                    <Button size="sm" variant="secondary" type="button" onClick={() => setEditingReceiptId(null)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {receipt.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {receipt.receiptDate && (
                        <span>{formatDisplayDate(receipt.receiptDate)}</span>
                      )}
                      {receipt.amount !== null && (
                        <span className="font-medium text-gray-700">{formatCurrency(receipt.amount)}</span>
                      )}
                      {receipt.hasFile && (
                        <button
                          onClick={() => handleDownload(receipt.id)}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {receipt.fileName}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {editable && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(receipt)}
                        >
                          Edit
                        </Button>
                        <ReceiptUpload
                          onUpload={(file) => handleUpload(receipt.id, file)}
                          onError={setError}
                          isUploading={uploadMutation.isPending}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDeleteId(receipt.id)}
                          className="text-red-600"
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Receipt"
        message="Are you sure you want to delete this receipt?"
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
